import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { catalogService } from '../../shared/services';
import { BookOpen, Award, Clock } from 'lucide-react';

export const TrackDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();

  // Buscar todas as trilhas e filtrar pelo ID (pois o endpoint de detalhe não retorna cursos)
  const { data: trilhas, isLoading } = useQuery({
    queryKey: ['trilhas'],
    queryFn: catalogService.getTrilhas,
    retry: 1,
  });

  const trilha = trilhas?.find(t => t.id === slug);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!trilha) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Trilha não encontrada</h2>
          <Link to="/trilhas" className="text-primary-600 hover:text-primary-700">
            Voltar às trilhas
          </Link>
        </div>
      </div>
    );
  }

  const totalCargaHoraria = trilha.duracaoTotalMinutos || 0;
  const totalHoras = Math.floor(totalCargaHoraria / 60);
  const totalMinutos = totalCargaHoraria % 60;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-8 py-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-white bg-opacity-90 text-purple-800">
              {trilha.totalCursos || 0} curso{trilha.totalCursos !== 1 ? 's' : ''}
            </span>
            {trilha.progresso !== undefined && trilha.progresso > 0 && (
              <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm">
                {trilha.progresso}% concluído
              </span>
            )}
          </div>

          <h1 className="text-4xl font-bold mb-4">{trilha.nome}</h1>
          <p className="text-lg text-purple-100 mb-6 max-w-3xl">{trilha.descricao}</p>

          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <BookOpen size={20} />
              <span>{trilha.totalCursos || 0} curso{trilha.totalCursos !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={20} />
              <span>{totalHoras}h {totalMinutos}min</span>
            </div>
            {trilha.progresso !== undefined && (
              <div className="flex items-center gap-2">
                <Award size={20} />
                <span>Certificação ao concluir</span>
              </div>
            )}
          </div>

          {trilha.progresso !== undefined && (
            <div className="mt-6 max-w-md">
              <div className="flex items-center justify-between text-sm mb-2">
                <span>Seu progresso</span>
                <span className="font-semibold">{trilha.progresso}%</span>
              </div>
              <div className="w-full h-3 bg-white bg-opacity-20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white transition-all"
                  style={{ width: `${trilha.progresso}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cursos da trilha */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-12">Cursos desta Trilha</h2>

        {Array.isArray(trilha.cursos) && trilha.cursos.length > 0 ? (
          <div className="relative">
            {/* Linha vertical central */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-300 -translate-x-1/2 hidden md:block"></div>

            <div className="space-y-16">
              {trilha.cursos.map((curso: any, index: number) => {
                const progresso = curso.progresso || 0;
                const concluido = curso.concluido || false;
                const isEven = index % 2 === 0;

                return (
                  <div key={curso.id || index} id={`curso-${curso.id}`} className="relative">
                    {/* Ponto na timeline */}
                    <div className="absolute left-1/2 -translate-x-1/2 w-12 h-12 bg-gray-200 rounded-full border-4 border-white shadow-md z-10 hidden md:flex items-center justify-center">
                      <span className="text-sm font-bold text-gray-700">{index + 1}</span>
                    </div>

                    {/* Card - alterna entre esquerda e direita */}
                    <div className={`md:w-[calc(50%-3rem)] ${isEven ? 'md:mr-auto md:pr-12' : 'md:ml-auto md:pl-12'}`}>
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all group">
                        <div className="flex flex-col">
                          {/* Imagem */}
                          <div className="h-48 relative overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600">
                            {curso.capa ? (
                              <img
                                src={curso.capa}
                                alt={curso.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <BookOpen size={64} className="text-white opacity-50" />
                              </div>
                            )}
                            {/* Badge de nível sobreposto */}
                            <div className="absolute top-4 left-4">
                              <span className="px-3 py-1 bg-white bg-opacity-95 text-gray-900 text-xs font-bold rounded-full shadow-md">
                                {curso.level || 'Iniciante'}
                              </span>
                            </div>
                          </div>

                          {/* Conteúdo */}
                          <div className="p-6 flex flex-col justify-between">
                            <div>
                              <div className="flex items-center gap-3 mb-3">
                                {curso.durationMinutes && (
                                  <span className="text-sm text-gray-500 flex items-center gap-1">
                                    <Clock size={16} />
                                    {curso.durationMinutes} min
                                  </span>
                                )}
                                {concluido && (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                                    ✓ Concluído
                                  </span>
                                )}
                              </div>

                              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                                {curso.title}
                              </h3>

                              <p className="text-sm text-gray-600 leading-relaxed">
                                {curso.description}
                              </p>
                            </div>

                            {/* Progresso e Ações */}
                            <div className="mt-6">
                              {progresso > 0 && (
                                <div className="mb-4">
                                  <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                                    <span>Progresso</span>
                                    <span className="font-semibold">{progresso}%</span>
                                  </div>
                                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
                                      style={{ width: `${progresso}%` }}
                                    />
                                  </div>
                                </div>
                              )}

                              <div className="flex gap-3">
                                {progresso > 0 && !concluido ? (
                                  <Link 
                                    to={`/curso/${curso.id}`}
                                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-sm inline-flex items-center justify-center"
                                  >
                                    Continuar
                                  </Link>
                                ) : concluido ? (
                                  <Link 
                                    to={`/curso/${curso.id}`}
                                    className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors shadow-sm inline-flex items-center justify-center"
                                  >
                                    Revisar
                                  </Link>
                                ) : (
                                  <Link 
                                    to={`/curso/${curso.id}`}
                                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-sm inline-flex items-center justify-center"
                                  >
                                    Começar
                                  </Link>
                                )}
                                
                                <button 
                                  onClick={() => {
                                    // Scroll suave até o topo do card para ver o conteúdo
                                    const cardElement = document.getElementById(`curso-${curso.id}`);
                                    if (cardElement) {
                                      cardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                  }}
                                  className="px-6 py-2.5 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-lg transition-colors"
                                >
                                  Conteúdo
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-300">
            <BookOpen size={48} className="text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nenhum curso disponível nesta trilha ainda.</p>
            <p className="text-gray-400 text-sm mt-1">Os cursos serão adicionados em breve.</p>
          </div>
        )}
      </div>
    </div>
  );
}
